import _ from "lodash";
import { PencilLineOutlined } from "@toddle-edu/ds-icons";
import {
  ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
  STAFF_PROFILE_OTHER_STAFF_ACCESS_PERMISSION,
  STAFF_PROFILE_ACCESS_PERMISSION,
  IS_MFA_ENABLED,
} from "Constants/permissionConstants";
import { PARENT, STAFF, STUDENT, SELF } from "Constants/stringConstants";
import ACLStore from "lib/aclStore";
import { CURRICULUM_MODULE_MAP, MODULES } from "lib/staffPermissionMaps";
import { getIsAdminByRole } from "lib/staffPermissionUtils";
import { MODULE_CONFIGURATION_ID_MAP } from "Login/routes/Administrator/routes/StaffPermissions/modules/constants";
import { getSortedCurriculumModules } from "Login/routes/Administrator/routes/StaffPermissions/modules/feedHelpers";
import { parseConfigurationValue } from "Utils";
import {
  getConfigurationAccess,
  isRnPConfigurationEnabled,
} from "Login/routes/Administrator/modules/Utils";
import { DESIGNATION_CONSTANTS_MAP } from "modules/Services";
import ACLStorage from "lib/aclService";
import { isTimetableModuleEnabled } from "Courses/modules/utils";

/**
 * Teacher profile page tabs to display
 */
export const PROFILE_SECTION_NODES = {
  [PARENT]: [],
  [STUDENT]: [],
  [STAFF]: ["overview", "classes", "homeroom", "timetable", "permissions"],
};

/**
 * date format for this module
 */

export const dateFormat = {
  ymdFormat: "YYYY MM DD",
  userBirthDateFormat: "DD MMM YYYY",
};

/**
 * Teacher profile page tabs detail
 */
export const typesConfig = {
  overview: {
    value: "overview",
    label: "common:overview",
    perms: {
      [STAFF]: ["TeacherPortal:TeacherProfileOverview"],
    },
  },
  classes: {
    value: "classes",
    label: "common:class_plural",
    perms: {
      [STAFF]: ["TeacherPortal:TeacherProfileClasses"],
    },
  },
  homeroom: {
    value: "homeroom",
    label: "common:label_student_count_plural",
    labelProps: {
      label: "attendance:homeroom",
    },
    perms: {
      [STAFF]: ["TeacherPortal:TeacherProfileHomeroom"],
    },
  },
  timetable: {
    value: "timetable",
    label: "common:timetable",
    perms: {
      [STAFF]: ["TeacherPortal:TeacherProfileTimetable"],
    },
    otherCondition: () => isTimetableModuleEnabled(),
  },
  permissions: {
    value: "permission",
    label: "common:permission_plural",
    perms: {
      [STAFF]: ["TeacherPortal:StaffPermissionsTab"],
    },
    otherCondition: ({ showPermissionsTab }) => showPermissionsTab,
  },
};

/**
 * Overview page widgets
 * all constants for this page is below
 */
export const widgetTypesConfig = {
  PERSONAL_INFORMATION: {
    id: "PERSONAL_INFORMATION",
    value: "common:personal_info",
    editType: "EDIT",
    isAdminEdit: true,
    isSelfEdit: true,
    editTitle: "common:edit",
    icon: PencilLineOutlined,
    level: 2,
    perms: [],
  },
  THIRD_PARTY_ACCOUNT: {
    id: "THIRD_PARTY_ACCOUNT",
    value: "common:account_plural",
    level: 2,
    perms: ["TeacherPortal:TeacherProfileAccountConnect"],
  },
  MULTI_FACTOR_AUTHENTICATION: {
    id: "MULTI_FACTOR_AUTHENTICATION",
    value: "common:2fa",
    level: 3,
    perms: [IS_MFA_ENABLED],
  },
};

export const WIDGET_TYPES_NODES_LEFT = {
  [STAFF]: [widgetTypesConfig.PERSONAL_INFORMATION.id],
  [SELF]: [widgetTypesConfig.PERSONAL_INFORMATION.id],
};

export const WIDGET_TYPES_NODES_RIGHT = {
  [SELF]: [
    widgetTypesConfig.THIRD_PARTY_ACCOUNT.id,
    widgetTypesConfig.MULTI_FACTOR_AUTHENTICATION.id,
  ],
};

export const canOpenTeacherProfile = ({ userType }) => {
  return userType === STAFF;
};

export const showTeacherProfileResetPassword = () =>
  ACLStore.can("FeatureFlag:TeacherProfileResetPassword");

export const showTeacherProfileArchiveStaff = () =>
  ACLStore.can("FeatureFlag:TeacherProfileArchiveStaff");

export const canEditContact = () =>
  ACLStore.can("FeatureFlag:TeacherProfileEditContact");

export const canEditDOB = () =>
  ACLStore.can("FeatureFlag:TeacherProfileEditBirthDate");

export const canEditAddress = () =>
  ACLStore.can("FeatureFlag:TeacherProfileEditAddress");

export const canEditPersonalEmail = () =>
  ACLStore.can("FeatureFlag:TeacherProfileEditPersonalEmail");

export const canEditDisplayTitle = () => {
  return ACLStore.isStaffPermissionEnabled() || isRnPConfigurationEnabled();
};

export const showTeacherPronoun = () => {
  return ACLStore.can("FeatureFlag:EnableStaffPronounsInStaffProfile");
};

export const getCurriculumProgramJoinedTag = curriculumPrograms => {
  const curriculumProgramsAcronym = _.map(
    curriculumPrograms,
    curriculumProgram => curriculumProgram.acronym
  );
  let curriculumProgramsTag = "";
  if (_.size(curriculumPrograms) > 1) {
    const startArray = _.slice(
      curriculumProgramsAcronym,
      0,
      curriculumProgramsAcronym.length - 1
    );
    const lastElement =
      curriculumProgramsAcronym[curriculumProgramsAcronym.length - 1];

    curriculumProgramsTag = _.join(
      [_.join(startArray, ", "), lastElement],
      " & "
    );
  } else if (_.size(curriculumPrograms) == 1) {
    curriculumProgramsTag = curriculumProgramsAcronym[0];
  }
  return curriculumProgramsTag;
};

export const getAllTabs = ({ t, userType, tabsData }) => {
  const tabList = PROFILE_SECTION_NODES[userType];

  return _.reduce(
    tabList,
    (result, data) => {
      const {
        label,
        value,
        labelProps = {},
        perms = {},
        otherCondition,
      } = typesConfig[data];

      const { [userType]: permissionsByUserType } = perms;

      let isAllowed = !!permissionsByUserType;

      isAllowed &= !_.size(permissionsByUserType)
        ? true
        : ACLStore.can(permissionsByUserType, "all");

      isAllowed &= !otherCondition ? true : otherCondition(tabsData);

      if (isAllowed) {
        const labelParams = _.mapValues(labelProps, value => t(value));
        result.push({ label: t(label, labelParams), value: value });
      }
      return result;
    },
    []
  );
};

export const getModuleConfigurations = ({ type }) => {
  const permissionsList = _.get(CURRICULUM_MODULE_MAP[type], "modules");
  const sortedCurriculumModules = getSortedCurriculumModules(permissionsList);

  const moduleConfigurations = _.map(sortedCurriculumModules, module => {
    return {
      id: MODULES[module].id,
      label: MODULES[module].id,
      locale: MODULES[module].label,
      entity: MODULES[module].id,
      perm: MODULES[module].perm,
      shouldHideModuleColumn: MODULES[module].shouldHideModuleColumn,
    };
  });

  return moduleConfigurations;
};

export const moduleConfigurationMap = ({ modules, staffPermissions }) => {
  return _.reduce(
    modules,
    (results, eachModule) => {
      const {
        id,
        locale,
        perm,
        shouldHideModuleColumn = _.stubFalse,
      } = eachModule;

      const moduleConfigurationId = MODULE_CONFIGURATION_ID_MAP[id];

      const moduleConfigurationAccessLevelOptions = _.map(
        _.get(MODULES, [id, "values"]),
        ({ id: accessString, labelLocale, Icon }) => ({
          value: accessString,
          label: labelLocale,
          Icon: Icon,
        })
      );

      const configurationForModule = _.find(
        staffPermissions,
        staffPermission =>
          _.toString(staffPermission.id) === _.toString(moduleConfigurationId)
      );

      const serverSelectedConfigurationValueOption = _.find(
        moduleConfigurationAccessLevelOptions,
        option =>
          option.value === parseConfigurationValue(configurationForModule)
      );

      let moduleAvailability = true;
      if (perm) {
        const moduleAvailabilityConfig = _.find(
          staffPermissions,
          staffPermission =>
            _.toString(staffPermission.configuration) === _.toString(perm)
        );

        moduleAvailability =
          moduleAvailabilityConfig &&
          parseConfigurationValue(moduleAvailabilityConfig);
      }

      if (moduleAvailability && !shouldHideModuleColumn(ACLStore)) {
        results.push({
          moduleConfigurationAccessLevelOptions,
          serverSelectedConfigurationValueOption,
          id,
          locale,
        });
      }

      return results;
    },
    []
  );
};

export const getUpdatedConfigurations = ({
  selectedOptions,
  staffPermissions,
}) => {
  const resultWithNewAndOldValues = _.map(selectedOptions, option => {
    const configurationId = MODULE_CONFIGURATION_ID_MAP[option.id];
    const originalConfiguration = _.find(
      staffPermissions,
      permission => _.get(permission, "id") === _.toString(configurationId)
    );

    const originalValue = parseConfigurationValue(originalConfiguration);

    return {
      value: JSON.stringify(
        _.get(option, "selectedConfigurationValueOption.value")
      ),
      id: configurationId,
      isValueChanged:
        _.get(option, "selectedConfigurationValueOption.value") !==
        originalValue,
    };
  });

  return _.map(
    _.filter(resultWithNewAndOldValues, result => result.isValueChanged),
    ({ value, id }) => ({ value, id })
  );
};

export const canAccessPermissionsTab = ({
  isSelfProfile,
  userCurriculumPrograms,
  profileDesignation,
}) => {
  if (ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)) {
    return false;
  }

  const hasAdminAccessForAnyOneCurriculum = hasAdminAccessToPermissionTab({
    userCurriculumPrograms,
  });

  return (
    (hasAdminAccessForAnyOneCurriculum || isSelfProfile) &&
    _.size(userCurriculumPrograms) > 0 &&
    profileDesignation === DESIGNATION_CONSTANTS_MAP.TEACHER.id
  );
};

export const hasAdminAccessToPermissionTab = ({ userCurriculumPrograms }) => {
  return getConfigurationAccess({
    curriculumIds: _.map(userCurriculumPrograms, "id"),
    moduleKey: MODULES.STAFF_PERMISSIONS.id,
  });
};

const getCourseGradeFilters = ({ academicYearId }) => {
  return {
    academicYearId,
  };
};

export const getCourseGradeFiltersMemoized = _.memoize(
  getCourseGradeFilters,
  JSON.stringify
);

export const getUserCourseMapFilters = ({
  archivalState = "ALL",
  academicYearIds,
}) => {
  return {
    archivalState,
    ...(academicYearIds ? { academicYearIds } : {}),
  };
};

export const getUserCourseMapFiltersMemoized = _.memoize(
  getUserCourseMapFilters,
  JSON.stringify
);

export const getCanManageStaffProfile = () => {
  // TODO: remove admin check once RNP is fully implemented
  return ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)
    ? ACLStorage.getModuleSettingsValue(
        STAFF_PROFILE_OTHER_STAFF_ACCESS_PERMISSION
      )
    : getIsAdminByRole();
};

export const getCanManageOwnStaffProfile = () => {
  return ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)
    ? ACLStorage.getModuleSettingsValue(STAFF_PROFILE_ACCESS_PERMISSION)
    : getIsAdminByRole();
};

